import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { formatSequencePosition } from '../../lib/vaultSequence';

interface VaultSequenceDialogProps {
  open: boolean;
  documentName: string;
  currentPosition?: number;
  totalDocuments: number;
  onClose: () => void;
  onSave: (position: number) => Promise<void>;
}

export function VaultSequenceDialog({
  open,
  documentName,
  currentPosition,
  totalDocuments,
  onClose,
  onSave,
}: VaultSequenceDialogProps) {
  const [position, setPosition] = useState(String(currentPosition ?? 1));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setPosition(String(currentPosition ?? (totalDocuments || 1)));
      setError(null);
    }
  }, [open, currentPosition, totalDocuments]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = Number(position);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > totalDocuments) {
      setError(`Choose a position between 1 and ${totalDocuments}.`);
      return;
    }
    if (parsed === currentPosition) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onSave(Math.trunc(parsed));
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update document sequence.');
    } finally {
      setLoading(false);
    }
  };

  const previewPosition = Number(position);
  const previewLabel = Number.isFinite(previewPosition) && previewPosition >= 1
    ? formatSequencePosition(Math.min(Math.trunc(previewPosition), totalDocuments))
    : null;

  return (
    <div className="wfx-vault-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-vault-modal glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-sequence-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-vault-modal-head">
          <h2 id="vault-sequence-title">Set document sequence</h2>
          <button type="button" className="ghost-icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <form className="wfx-vault-upload-form" onSubmit={handleSubmit}>
          <p className="wfx-muted">
            Choose where <strong>{documentName}</strong> appears when documents are attached in order.
          </p>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <label className="wfx-vault-select">
            <span>Position in sequence</span>
            <input
              type="number"
              min={1}
              max={totalDocuments}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              autoFocus
              required
            />
          </label>
          {previewLabel && (
            <p className="wfx-vault-sequence-preview">
              This document will appear at <strong>{previewLabel}</strong> position.
            </p>
          )}
          <div className="wfx-vault-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={loading || totalDocuments < 1}>
              {loading ? 'Saving…' : 'Save position'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
